import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Container } from 'reactstrap'
import moment from 'moment'
import {
  getMonthBalance,
  getHoursBank,
  getExpedientStatus,
  getLastExpedientInput,
  getLastExpedientOutput,
} from '../../store/ducks/humanResources'
import { getPfProjectsByPfId } from '../../utils/fc'
import WorkloadGraph from '../Dashboard/WorkloadGraph'
import ActivitiesGraph from '../Dashboard/ActivitiesGraph'
import Agenda from '../Dashboard/Agenda'
import ProjectLinks from '../Dashboard/ProjectsLinks'
import ProductsApprovement from '../Dashboard/ProductsApprovement'
import EPApprovement from '../Dashboard/EPApprovement'
import FaturamentoProximosCards from '../Dashboard/FaturamentoProximosCards'
import MaterialTable from '../../components/MaterialTable/MaterialTable'
import './DashboardErp.css'

/**
 * Dashboard ERP — Clássico em Z (WF2).
 * Grade 4×25%: KPIs → herói 75% / apoio 25% → base 50/50.
 * Rota de teste — não substitui /dashboard.
 */
export default function DashboardErp() {
  const dispatch = useDispatch()
  const auth = useSelector((s) => s.auth)
  const hr = useSelector((s) => s.humanResources)
  const user = auth?.user

  const [time, setTime] = useState(moment())
  const [projects, setProjects] = useState(null)
  const [duration, setDuration] = useState('Carregando...')

  useEffect(() => {
    dispatch(getMonthBalance())
    dispatch(getHoursBank())
    dispatch(getLastExpedientInput())
    dispatch(getLastExpedientOutput())
    dispatch(getExpedientStatus())
  }, [dispatch])

  useEffect(() => {
    const tick = setInterval(() => setTime(moment()), 1000)
    return () => clearInterval(tick)
  }, [])

  useEffect(() => {
    if (!user?.pf_id) return undefined
    let cancelled = false
    getPfProjectsByPfId(user.pf_id).then((res) => {
      if (!cancelled) setProjects(res)
    })
    return () => {
      cancelled = true
    }
  }, [user?.pf_id])

  useEffect(() => {
    const tick = setInterval(() => {
      if (hr.lastExpedientInput) {
        const start = moment(hr.lastExpedientInput, 'HH:mm')
        const diff = moment().diff(start)
        setDuration(moment.utc(diff).format('HH[h]mm[min]'))
      } else {
        setDuration(null)
      }
    }, 1000)
    return () => clearInterval(tick)
  }, [hr.lastExpedientInput])

  const monthValue =
    hr.monthBalance?.t_timestamp != null ? hr.monthBalance.t_timestamp : 'Carregando...'
  const monthPct = hr.monthBalance?.percentual != null ? hr.monthBalance.percentual : 0
  const bankValue =
    hr.hoursBank?.banco_acumulado_timestamp != null
      ? hr.hoursBank.banco_acumulado_timestamp
      : 'Carregando...'
  const bankVar =
    hr.hoursBank?.banco_acumulado_var_abs != null ? hr.hoursBank.banco_acumulado_var_abs : 0

  const projectCount = Array.isArray(projects) ? projects.length : null
  const clock = moment(time).format('dddd, DD [de] MMMM [de] YYYY, HH:mm')

  const showProducts = user && user.dpto_id === 4
  const showEp =
    user &&
    user.pf_cargo &&
    (user.pf_cargo.includes('Diretor') ||
      user.pf_cargo.includes('Gerente') ||
      user.pf_cargo.includes('Desenvolvedor'))

  return (
    <div className="page-content dash-erp">
      <Container fluid>
        <div className="dash-erp__banner">
          <span>
            <strong>Dashboard ERP</strong>
            <span className="dash-erp__mark">WF2</span>
            {' — '}
            Clássico Z · KPIs 4×25% · herói 75/25 · base 50/50.
          </span>
          <Link to="/dashboard">Abrir dashboard original</Link>
        </div>

        <header className="dash-erp__titlebar">
          <h1>Dashboard</h1>
          <p className="dash-erp__clock">{clock}</p>
        </header>

        <section className="dash-erp__z" aria-label="Layout Clássico Z">
          {/* Linha KPI — 4 × 25% */}
          <article className="dash-erp__kpi">
            <span className="dash-erp__kpi-label">Expediente atual</span>
            <span className="dash-erp__kpi-value">
              {hr.getLastExpedientInputIsLoading ? 'Carregando...' : duration || 'Não iniciado'}
            </span>
            <span className="dash-erp__kpi-foot">
              {hr.lastExpedientInput
                ? `Entrada: ${moment(hr.lastExpedientInput, 'HH:mm').calendar().toLowerCase()}`
                : hr.lastExpedientOutput
                  ? `Saída: ${moment(hr.lastExpedientOutput, 'DD/MM/YYYY h:mm').calendar().toLowerCase()}`
                  : '—'}
            </span>
          </article>

          <article className="dash-erp__kpi">
            <span className="dash-erp__kpi-label">Carga horária restante no mês</span>
            <span className="dash-erp__kpi-value">{monthValue}</span>
            <span className="dash-erp__kpi-foot">
              <em>{monthPct ? `${((1 - monthPct) * 100).toFixed(0)}%` : '%'}</em>
              realizado até o momento
            </span>
          </article>

          <article className="dash-erp__kpi">
            <span className="dash-erp__kpi-label">Banco de horas</span>
            <span className="dash-erp__kpi-value">{bankValue}</span>
            <span className="dash-erp__kpi-foot">
              <em>{bankVar}</em>
              em relação ao mês anterior
            </span>
          </article>

          <article className="dash-erp__kpi">
            <span className="dash-erp__kpi-label">Meus projetos</span>
            <span className="dash-erp__kpi-value">
              {projectCount == null ? 'Carregando...' : projectCount}
            </span>
            <span className="dash-erp__kpi-foot">vinculados ao seu perfil</span>
          </article>

          {/* Herói 75% */}
          <div className="dash-erp__hero">
            <div className="dash-erp__zone dash-erp__zone--flush">
              <div className="dash-erp__zone-head">
                <strong>Faturamento próximo</strong>
                <span className="dash-erp__zone-tag">Herói · 75%</span>
              </div>
              <div className="dash-erp__embed">
                <FaturamentoProximosCards />
              </div>
            </div>

            <div className="dash-erp__zone dash-erp__zone--flush">
              <div className="dash-erp__zone-head">
                <strong>Carga horária mensal</strong>
                <span className="dash-erp__zone-tag">Gráfico herói</span>
              </div>
              <WorkloadGraph />
            </div>
          </div>

          {/* Apoio 25% */}
          <aside className="dash-erp__support" aria-label="Coluna apoio 25%">
            {user && projects && (
              <div className="dash-erp__zone dash-erp__zone--flush">
                <div className="dash-erp__zone-head">
                  <strong>Meus projetos</strong>
                  <span className="dash-erp__zone-tag">Apoio</span>
                </div>
                <ProjectLinks userOikosId={user.pf_id} projects={projects} />
              </div>
            )}

            {showProducts && (
              <div className="dash-erp__zone dash-erp__zone--flush">
                <ProductsApprovement userOikosId={user.pf_id} />
              </div>
            )}

            {showEp && (
              <div className="dash-erp__zone dash-erp__zone--flush">
                <EPApprovement userOikosId={user.pf_id} />
              </div>
            )}

            <div className="dash-erp__zone dash-erp__zone--flush">
              <div className="dash-erp__zone-head">
                <strong>Agenda</strong>
                <Link className="dash-erp__btn dash-erp__btn--accent" to="/agenda">
                  Ver tudo
                </Link>
              </div>
              <Agenda />
            </div>
          </aside>

          {/* Base 50/50 */}
          <div className="dash-erp__base">
            <div className="dash-erp__zone dash-erp__zone--flush">
              <div className="dash-erp__zone-head">
                <strong>Distribuição das atividades</strong>
                <span className="dash-erp__zone-tag">Base · 50%</span>
              </div>
              <ActivitiesGraph />
            </div>
          </div>

          <div className="dash-erp__base">
            <div className="dash-erp__zone">
              <div className="dash-erp__zone-head">
                <strong>Atividades / materiais</strong>
                <span className="dash-erp__zone-tag">Base · 50%</span>
              </div>
              {user?.pf_id ? <MaterialTable pf_id={user.pf_id} /> : null}
            </div>
          </div>
        </section>
      </Container>
    </div>
  )
}
